/**
 * Query Processing Utilities for Enhanced Quick Search
 * Leverages existing synonym mappings and provides client-side NLP capabilities
 */

// Reuse the same synonym mappings from server optimizedSearchService.js
const LEGAL_PRACTICE_AREAS = {
  'divorce': ['family law', 'matrimonial', 'separation', 'child custody', 'domestic relations', 'marital', 'custody'],
  'family': ['divorce', 'matrimonial', 'child custody', 'separation', 'adoption', 'domestic relations'],
  'property': ['conveyancing', 'real estate', 'landlord tenant', 'commercial property', 'residential'],
  'conveyancing': ['property', 'real estate', 'residential', 'commercial property', 'buy', 'sell', 'purchase'],
  'immigration': ['visa', 'asylum', 'citizenship', 'work permits', 'settlement', 'nationality'],
  'criminal': ['defence', 'prosecution', 'magistrates', 'crown court', 'criminal law'],
  'employment': ['workplace', 'discrimination', 'unfair dismissal', 'tribunal', 'redundancy'],
  'personal injury': ['accident', 'compensation', 'medical negligence', 'claims'],
  'commercial': ['business law', 'corporate', 'contracts', 'company law'],
  'litigation': ['dispute resolution', 'court proceedings', 'civil litigation'],
  'probate': ['wills', 'estate planning', 'inheritance', 'estate administration']
};

// Common UK cities and regions
const UK_LOCATIONS = [
  'london', 'city of london', 'greater london', 'east london', 'west london', 'north london', 'south london',
  'manchester', 'birmingham', 'leeds', 'glasgow', 'edinburgh', 
  'bristol', 'liverpool', 'cardiff', 'sheffield', 'newcastle', 'nottingham',
  'brighton', 'oxford', 'cambridge', 'reading', 'coventry', 'leicester',
  'hull', 'stoke', 'derby', 'portsmouth', 'southampton', 'swansea'
];

// Legal qualifiers and requirements
const LEGAL_QUALIFIERS = {
  'experienced': ['senior', 'specialist', 'expert', 'qualified', 'experienced'],
  'affordable': ['cost-effective', 'reasonable', 'budget', 'cheap', 'low cost'],
  'quick': ['fast', 'urgent', 'immediate', 'expedited', 'rapid'],
  'rating': ['rated', 'reviewed', 'star', 'stars', 'rating', 'highly rated'],
  'large': ['big', 'large', 'major', 'leading', 'top'],
  'local': ['near', 'nearby', 'local', 'close']
};

/**
 * Calculate Levenshtein distance for spell checking
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  const n = str2.length;
  const m = str1.length;

  if (n === 0) return m;
  if (m === 0) return n;

  for (let i = 0; i <= n; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= m; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[n][m];
}

/**
 * Get spell suggestions for a term
 */
function getSpellSuggestions(term, dictionary, maxSuggestions = 3) {
  if (!term || term.length < 3) return [];

  const suggestions = [];
  const lowerTerm = term.toLowerCase();

  for (const dictTerm of dictionary) {
    const distance = levenshteinDistance(lowerTerm, dictTerm.toLowerCase());
    const threshold = Math.max(1, Math.floor(term.length * 0.4)); // Allow 40% character difference

    if (distance <= threshold && distance > 0) {
      suggestions.push({
        original: term,
        suggestion: dictTerm,
        distance: distance,
        confidence: 1 - (distance / term.length)
      });
    }
  }

  return suggestions
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxSuggestions);
}

/**
 * Detect practice area from query
 */
function detectPracticeArea(query) {
  const lowerQuery = query.toLowerCase();
  const detected = [];

  for (const [area, synonyms] of Object.entries(LEGAL_PRACTICE_AREAS)) {
    if (lowerQuery.includes(area) || synonyms.some(syn => lowerQuery.includes(syn))) {
      detected.push({
        area: area,
        confidence: lowerQuery.includes(area) ? 1.0 : 0.8,
        matchedTerm: lowerQuery.includes(area) ? area : synonyms.find(syn => lowerQuery.includes(syn))
      });
    }
  }

  return detected.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Detect location from query
 */
function detectLocation(query) {
  const lowerQuery = query.toLowerCase();
  const detected = UK_LOCATIONS.find(city => {
    // Use word boundaries to avoid partial matches
    const regex = new RegExp(`\\b${city}\\b`, 'i');
    return regex.test(lowerQuery);
  });

  // Normalize all London variations to 'london'
  if (detected && detected.includes('london')) {
    return 'london';
  }
  
  return detected || null;
}

/**
 * Detect qualifiers/requirements from query
 */
function detectQualifiers(query) {
  const lowerQuery = query.toLowerCase();
  const detected = [];

  for (const [qualifier, keywords] of Object.entries(LEGAL_QUALIFIERS)) {
    const matched = keywords.find(keyword => lowerQuery.includes(keyword));
    if (matched) {
      detected.push({
        qualifier: qualifier,
        matchedTerm: matched,
        confidence: 0.9
      });
    }
  }

  return detected;
}

/**
 * Main query preprocessing function
 * Converts natural language to enhanced search terms
 */
export function preprocessQuery(userQuery) {
  if (!userQuery || typeof userQuery !== 'string') {
    return {
      originalQuery: userQuery,
      enhancedTerms: [],
      practiceAreas: [],
      location: null,
      qualifiers: [],
      suggestions: [],
      searchExplanation: ''
    };
  }

  const query = userQuery.trim();
  
  // Detect components
  const practiceAreas = detectPracticeArea(query);
  const location = detectLocation(query);
  const qualifiers = detectQualifiers(query);

  // Generate enhanced search terms
  const enhancedTerms = [];
  
  // Add detected practice areas and their synonyms
  practiceAreas.forEach(pa => {
    enhancedTerms.push(pa.area);
    if (LEGAL_PRACTICE_AREAS[pa.area]) {
      enhancedTerms.push(...LEGAL_PRACTICE_AREAS[pa.area]);
    }
  });

  // Add location
  if (location) {
    enhancedTerms.push(location);
  }

  // Add qualifier terms
  qualifiers.forEach(q => {
    enhancedTerms.push(q.qualifier);
  });

  // Check for spelling suggestions
  const allLegalTerms = [
    ...Object.keys(LEGAL_PRACTICE_AREAS),
    ...Object.values(LEGAL_PRACTICE_AREAS).flat(),
    ...UK_LOCATIONS
  ];

  const words = query.toLowerCase().split(/\s+/);
  const suggestions = [];

  words.forEach(word => {
    if (word.length >= 3) {
      const spellSuggestions = getSpellSuggestions(word, allLegalTerms, 2);
      suggestions.push(...spellSuggestions);
    }
  });

  // Generate search explanation
  let explanation = '';
  if (practiceAreas.length > 0 || location || qualifiers.length > 0) {
    const parts = [];
    if (practiceAreas.length > 0) {
      parts.push(`${practiceAreas[0].area} law`);
    }
    if (location) {
      parts.push(`in ${location}`);
    }
    if (qualifiers.length > 0) {
      parts.push(qualifiers.map(q => q.qualifier).join(', '));
    }
    explanation = `Searching for: ${parts.join(' ')}`;
  }

  return {
    originalQuery: query,
    enhancedTerms: [...new Set(enhancedTerms)], // Remove duplicates
    practiceAreas: practiceAreas,
    location: location,
    qualifiers: qualifiers,
    suggestions: suggestions,
    searchExplanation: explanation || `Searching for: "${query}"`
  };
}

/**
 * Convert preprocessed query to fuzzy search criteria
 */
export function queryToCriteria(preprocessedQuery) {
  const criteria = {
    name: '',
    practiceAreas: '',
    organisationType: '',
    sraNumber: '',
    address: '',
    phone: '',
    email: ''
  };

  // Set practice area if detected
  if (preprocessedQuery.practiceAreas.length > 0) {
    const primaryArea = preprocessedQuery.practiceAreas[0];
    // Map to display names that match your existing practice areas
    const practiceAreaMap = {
      'divorce': 'Family / Matrimonial',
      'family': 'Family / Matrimonial',
      'property': 'Property - Residential',
      'conveyancing': 'Property - Residential',
      'immigration': 'Immigration',
      'criminal': 'Criminal',
      'employment': 'Employment',
      'personal injury': 'Personal Injury',
      'commercial': 'Commercial',
      'litigation': 'Litigation',
      'probate': 'Probate and Estate Administration'
    };
    
    criteria.practiceAreas = practiceAreaMap[primaryArea.area] || primaryArea.area;
  }

  // Set location in address field
  if (preprocessedQuery.location) {
    criteria.address = preprocessedQuery.location;
  }

  // If no specific field mapping, put the original query in name field
  if (!criteria.practiceAreas && !criteria.address) {
    criteria.name = preprocessedQuery.originalQuery;
  }

  return criteria;
}

/**
 * Generate enhanced Fuse.js options with better scoring
 */
export function getEnhancedFuseOptions() {
  return {
    includeScore: true,
    threshold: 0.4, // Slightly more lenient for natural language queries
    keys: [
      { name: 'practice_name', weight: 3 }, // Increased weight for firm names
      { name: 'practice_areas.name', weight: 4 }, // Highest weight for practice areas
      { name: 'offices.town', weight: 2.5 }, // High weight for location
      { name: 'offices.address1', weight: 1.5 },
      { name: 'offices.postcode', weight: 1.5 },
      { name: 'sra_number', weight: 2 },
      { name: 'organisation_type', weight: 1 },
      { name: 'email', weight: 1 },
      { name: 'phone', weight: 1 },
      'offices.address2',
      'offices.phone_number',
      'offices.email'
    ],
    ignoreLocation: true,
    useExtendedSearch: true,
    sortFn: (a, b) => a.score - b.score // Sort by score ascending (lower is better)
  };
}

export { LEGAL_PRACTICE_AREAS, UK_LOCATIONS, LEGAL_QUALIFIERS }; 